import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, FormControl } from '@mui/material';
import {
    AnnualLeaveBalance,
    Employee
} from '../../api/EmployeeApi';
import { Button } from '../../components/Button';
import { Textarea } from '../../components/Textarea';
import { Item } from 'react-stately';
import { Select } from '../../components/Select';
import { Modal, ModalActions } from '../../components/Modal';
import {
    LeaveRequestCreateRequest,
    LeaveRequestValidationRequest,
    LeaveRequestValidationResult
} from '../../api/RequestApi';
import { DateRangePicker } from '../../components/DateRangePicker';
import { DateRange } from '@react-types/calendar';
import { DateUtils } from '../../util/DateUtils';
import Spinner from '../../ui/Spinner';
import { RequestErrorCode } from '../../api/types';
import { ErrorAlert } from "../../components/ErrorAlert";
import { SuccessAlert } from "../../components/SuccessAlert";
import { useTranslation } from "react-i18next";
import useLeaveTypes from "../../hooks/useLeaveTypes";
import {CalendarDate} from "@internationalized/date";
import {fetchMyLeaveBalance} from "../../api/MeApi";


interface LeaveRequestModalProps {
    selfMode: boolean;
    open: boolean;
    onClose: () => void;
    onSubmit: (requestData: LeaveRequestCreateRequest) => Promise<void>;
    onValidate: (requestData: LeaveRequestValidationRequest) => Promise<LeaveRequestValidationResult | null>;
    submitting?: boolean;
    validating?: boolean;
    employees?: Employee[];
    selectedEmployee: Employee;
    initialLeaveStart?: Date;
    initialLeaveEnd?: Date;
}

const LeaveRequestModal: React.FC<LeaveRequestModalProps> = ({
                                                                 selfMode = true,
                                                                 open,
                                                                 onClose,
                                                                 onSubmit,
                                                                 onValidate,
                                                                 submitting = false,
                                                                 validating = false,
                                                                 employees,
                                                                 selectedEmployee,
                                                                 initialLeaveStart,
                                                                 initialLeaveEnd
                                                             }) => {

    const { t } = useTranslation();
    const [loading, setLoading] = useState<boolean>(false);

    // Data
    const [annualLeaveBalance, setAnnualLeaveBalance] = useState<AnnualLeaveBalance>(); // TODO: This should be loaded in the widget itself so that a reload is possible after cancel
    const { leaveTypes, loading: loadingLeaveTypes } = useLeaveTypes();

    // Form state
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>(selectedEmployee.employeeId);
    const [selectedLeaveType, setSelectedLeaveType] = useState<number>();
    const [startEnd, setStartEnd] = useState<DateRange | null>(null);
    const [comment, setComment] = useState<string>('');

    // Messages
    const [calculatedVacationDays, setCalculatedVacationDays] = useState<number>();

    // Error handling
    const [error, setError] = useState<string | null>(null);
    const [errorCode, setErrorCode] = useState<RequestErrorCode | null>(null);

    useEffect(() => {
        loadAnnualLeaveBalance();
    }, [selectedEmployeeId]);

    function dateToCalendarDate(date: Date): CalendarDate {
        return new CalendarDate(
            date.getFullYear(),
            date.getMonth() + 1, // JavaScript months are 0-indexed, CalendarDate expects 1-12
            date.getDate()
        );
    }

    useEffect(() => {
        if (initialLeaveStart && initialLeaveEnd) {
            setStartEnd({
                start: dateToCalendarDate(initialLeaveStart),
                end: dateToCalendarDate(initialLeaveEnd)
            });
        }
    }, [initialLeaveStart, initialLeaveEnd]);

    useEffect(() => {
        if (open) {
            resetForm();
        }
    }, [open]);

    useEffect(() => {
        validateRequest();
    }, [startEnd, selectedLeaveType]);

    const loadAnnualLeaveBalance = useCallback(async () => {
        if (!selectedEmployeeId) {
            return;
        }

        setLoading(true);
        try {
            const response = await fetchMyLeaveBalance(); // TODO: This is a bug, as the same component will be used to create leave requests for anyone
            setAnnualLeaveBalance(response.data);
        } catch (error) {
            console.error('Error loading annual leave balance:', error);
        } finally {
            setLoading(false);
        }
    }, [selectedEmployeeId]);

    const validateRequest = useCallback(async () => {
        if (!startEnd || !selectedLeaveType) {
            return;
        }

        setError(null);
        setErrorCode(null);

        const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const { start, end } = DateUtils.convertDateRangeToJS(startEnd, localTimeZone);

        const validationRequest: LeaveRequestValidationRequest = {
            employeeId: selectedEmployeeId,
            startDate: start!,
            endDate: end!,
            leaveTypeId: selectedLeaveType
        };

        try {
            // Call validation through parent (which calls hook)
            const validationResult = await onValidate(validationRequest);

            if (validationResult) {
                setCalculatedVacationDays(validationResult.vacationDays);
            }
        } catch (err: any) {
            // Extract error code and message from ApiError
            const code = err?.code || RequestErrorCode.UNEXPECTED_ERROR;
            setErrorCode(code);
            setError(err?.message || 'Validation failed');
        }
    }, [selectedEmployeeId, startEnd, selectedLeaveType, onValidate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedLeaveType || !startEnd) {
            return;
        }

        setError(null);
        setErrorCode(null);

        const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const { start, end } = DateUtils.convertDateRangeToJS(startEnd, localTimeZone);

        const createRequest: LeaveRequestCreateRequest = {
            employeeId: selectedEmployeeId,
            startDate: start!,
            endDate: end!,
            leaveTypeId: selectedLeaveType,
            comment: comment
        };

        try {
            await onSubmit(createRequest);
            // Success - parent will handle closing and notifications
            onClose();
        } catch (err: any) {
            // Extract error code and message from ApiError
            const code = err?.code || RequestErrorCode.UNEXPECTED_ERROR;
            setErrorCode(code);
            setError(err?.message || 'Failed to create request');
        }
    };

    const handleDateRangeChange = (dateRange: DateRange) => {
        setStartEnd(dateRange);
    };

    const resetForm = () => {
        setSelectedLeaveType(undefined);
        setStartEnd(null);
        setComment('');
        setError(null);
        setErrorCode(null);
        setCalculatedVacationDays(undefined);

        // Set initial dates if provided, otherwise clear
        if (initialLeaveStart && initialLeaveEnd) {
            setStartEnd({
                start: dateToCalendarDate(initialLeaveStart),
                end: dateToCalendarDate(initialLeaveEnd)
            });
        } else {
            setStartEnd(null);
        }
    };

    const closeModal = () => {
        resetForm();
        onClose();
    };

    return (
        <Modal isOpen={open} onClose={closeModal} title={t('leaveRequest.modal.title')} size="lg">
            {loading ? (
                <Spinner />
            ) : (
                <>
                    <Box sx={{ mb: 3 }}>
                        <Typography sx={{ mb: 0 }} variant="body1" fontSize="small" gutterBottom>
                            {t('leaveRequest.modal.remainingBalance')}: {annualLeaveBalance?.remainingVacationDays} {t('leaveRequest.days')}
                        </Typography>
                        <Typography sx={{ mb: 0 }} variant="body1" fontSize="small" gutterBottom>
                            {t('leaveRequest.modal.usedBalance')}: {annualLeaveBalance?.usedVacationDays} {t('leaveRequest.days')}
                        </Typography>
                        {annualLeaveBalance && annualLeaveBalance.carriedOverVacationDays > 0 && (
                            <Typography sx={{ mb: 4 }} variant="body1" fontSize="small" gutterBottom>
                                {t('leaveRequest.balance.carriedOver')}: {annualLeaveBalance?.carriedOverVacationDays} {t('leaveRequest.days')}
                            </Typography>
                        )}
                    </Box>

                    <form onSubmit={handleSubmit}>
                        {!selfMode && employees && (
                            <Box sx={{ mb: 3 }}>
                                <FormControl fullWidth>
                                    <Select
                                        isRequired={true}
                                        placeholder={t('leaveRequest.form.selectEmployee')}
                                        isDisabled={submitting || selfMode}
                                        label={t('leaveRequest.form.employee')}
                                        items={employees.map(item => ({
                                            id: item.employeeId,
                                            name: item.preferredName
                                        }))}
                                        selectedKey={selectedEmployeeId || ''}
                                        onSelectionChange={(selected) =>
                                            setSelectedEmployeeId(selected as string)
                                        }
                                    >
                                        {(item) => <Item key={item.id}>{item.name}</Item>}
                                    </Select>
                                </FormControl>
                            </Box>
                        )}

                        <Box sx={{ mb: 3 }}>
                            <FormControl fullWidth>
                                <Select
                                    isRequired={true}
                                    placeholder={t('leaveRequest.modal.form.absenceTypePlaceholder')}
                                    isDisabled={submitting}
                                    label={t('leaveRequest.modal.form.absenceType')}
                                    items={leaveTypes.map(item => ({ id: item.id, name: item.name }))}
                                    selectedKey={selectedLeaveType || ''}
                                    onSelectionChange={(selected) =>
                                        setSelectedLeaveType(selected as number)
                                    }
                                >
                                    {(item) => <Item key={item.id}>
                                        {t(`leaveRequest.leaveType.${item.name}`)}
                                    </Item>}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <FormControl fullWidth>
                                <DateRangePicker
                                    label={t('leaveRequest.modal.form.dateRange')}
                                    isRequired={true}
                                    value={startEnd}
                                    onChange={handleDateRangeChange}
                                />
                            </FormControl>
                        </Box>

                        {calculatedVacationDays && !error && (
                            <SuccessAlert
                                message={t('leaveRequest.messages.validation', {
                                    vacationDays: calculatedVacationDays
                                })}
                                className={'mb-3'}
                            />
                        )}

                        <Box sx={{ mb: 3 }}>
                            <Textarea
                                label={t('leaveRequest.modal.form.comment')}
                                isRequired={false}
                                rows={2}
                                value={comment}
                                isDisabled={submitting}
                                onChange={(e) => setComment(e)}
                            />
                        </Box>

                        {error && (
                            <ErrorAlert message={error} />
                        )}

                        <ModalActions>
                            <Button
                                onClick={closeModal}
                                isDisabled={submitting}
                                variant="outlined"
                                color="primary"
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                type="submit"
                                isDisabled={submitting || !selectedLeaveType || !startEnd || validating}
                                loading={submitting}
                                variant="contained"
                                color="primary"
                            >
                                {t('leaveRequest.action.submit')}
                            </Button>
                        </ModalActions>
                    </form>
                </>
            )}
        </Modal>
    );
};

export default LeaveRequestModal;